import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { Plus, Edit2, Trash2, X, ListChecks, Play, Eye, Clock, GitCompareArrows, Flag, User as UserIcon, Download, Upload, RotateCw, Link2 } from "lucide-react";

function Modal({ open, onClose, title, children, width = 440 }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} onClick={onClose} />
      <div style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: width, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "var(--text-primary)", margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const TABS = ["Rule Sets", "Run History", "Multi-file Runs", "Chain Runs", "Exceptions"];

const FLAG_COLORS = {
  review: { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" },
  in_progress: { bg: "#DBEAFE", fg: "#1E40AF", border: "#BFDBFE" },
  escalated: { bg: "#FEE2E2", fg: "#991B1B", border: "#FECACA" },
  ignored: { bg: "#F3F4F6", fg: "#6B7280", border: "#E5E7EB" },
  resolved: { bg: "#D1FAE5", fg: "#065F46", border: "#A7F3D0" },
};

export default function DeptAdminDashboard() {
  const [tab, setTab] = useState("Rule Sets");
  const [ruleSets, setRuleSets] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [compareResult, setCompareResult] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [exceptions, setExceptions] = useState([]);
  const [deptUsers, setDeptUsers] = useState([]);
  const [drilldown, setDrilldown] = useState(null);
  const [selectedRS, setSelectedRS] = useState(new Set());
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  const [selectedEx, setSelectedEx] = useState(new Set());
  const [multiRuns, setMultiRuns] = useState([]);
  const [chainRuns, setChainRuns] = useState([]);
  const navigate = useNavigate();
  const importRSRef = useRef();

  const downloadJSON = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      alert("Export failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleImportRS = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/rule-sets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Imported ${res.data.imported} rule set(s)`);
      load();
    } catch (e) {
      alert("Import failed: " + (e.response?.data?.detail || e.message));
    } finally {
      importRSRef.current.value = "";
    }
  };

  const loadExceptions = async () => {
    try {
      const r = await api.get("/exceptions/");
      setExceptions(r.data);
    } catch (e) { /* ignore */ }
  };

  const loadDeptUsers = async () => {
    try {
      // Dept admin uses /admin/dept/users; superadmin uses /admin/users
      const r = await api.get("/admin/dept/users").catch(() => api.get("/admin/users"));
      setDeptUsers(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (tab === "Exceptions") { loadExceptions(); loadDeptUsers(); }
  }, [tab]);

  const transitionException = async (id, to_flag, note = "") => {
    await api.post(`/exceptions/${id}/transition`, { to_flag, note });
    loadExceptions();
  };

  const assignException = async (id, user_email) => {
    await api.post(`/exceptions/${id}/assign`, { user_email: user_email || null });
    loadExceptions();
  };

  const deleteException = async (id) => {
    if (!window.confirm("Delete this exception?")) return;
    await api.delete(`/exceptions/${id}`);
    loadExceptions();
  };

  const load = async () => {
    setLoading(true);
    try {
      const [rsRes, runsRes, mrRes, crRes] = await Promise.all([
        api.get("/rule-sets/"),
        api.get("/reconciliation/runs"),
        api.get("/multi-runs").catch(() => ({ data: [] })),
        api.get("/chain-runs").catch(() => ({ data: [] })),
      ]);
      setRuleSets(rsRes.data);
      setRuns(runsRes.data);
      setMultiRuns(mrRes.data || []);
      setChainRuns(crRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteRS = async (id) => {
    if (!window.confirm("Delete this rule set?")) return;
    await api.delete(`/rule-sets/${id}`);
    load();
  };

  const bulkDeleteRS = async () => {
    const ids = Array.from(selectedRS);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} rule set(s)? This cannot be undone.`)) return;
    await api.post("/rule-sets/bulk-delete", { ids });
    setSelectedRS(new Set());
    load();
  };

  const bulkDeleteRuns = async () => {
    const ids = Array.from(selectedRuns);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} run(s) AND their results/exceptions? This cannot be undone.`)) return;
    await api.post("/reconciliation/runs/bulk-delete", { ids });
    setSelectedRuns(new Set());
    load();
  };

  const bulkDeleteExceptions = async () => {
    const ids = Array.from(selectedEx);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} exception(s)?`)) return;
    await api.post("/exceptions/bulk-delete", { ids });
    setSelectedEx(new Set());
    loadExceptions();
  };

  const toggle = (setter, set, id) => {
    const s = new Set(set);
    if (s.has(id)) s.delete(id); else s.add(id);
    setter(s);
  };
  const toggleAll = (setter, items) => {
    const ids = items.map(x => x.id);
    setter(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const runComparison = async () => {
    if (!compareA || !compareB || compareA === compareB) return;
    try {
      const r = await api.get("/reconciliation/compare", { params: { run_a: compareA, run_b: compareB } });
      setCompareResult(r.data);
    } catch (e) {
      alert(`Compare failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const statusBadge = (status) => {
    if (status === "completed") return <span className="badge-matched">Completed</span>;
    if (status === "running") return <span className="badge-warning">Running</span>;
    if (status === "failed") return <span className="badge-unmatched">Failed</span>;
    return <span className="badge-partial">Pending</span>;
  };

  return (
    <Layout>
      <div className="animate-in">
        <div style={{ marginBottom: "2rem" }}>
          <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", margin: 0 }}>Department Admin</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginTop: "0.25rem" }}>Configure rule sets and monitor reconciliation runs</p>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
          {[
            { label: "Rule Sets", value: ruleSets.length, icon: ListChecks },
            { label: "Total Runs", value: runs.length, icon: Play },
            { label: "Completed", value: runs.filter(r => r.status === "completed").length, icon: Clock },
          ].map(s => (
            <div key={s.label} className="recon-card" style={{ padding: "1.25rem", display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ width: "2.5rem", height: "2.5rem", borderRadius: "0.5rem", background: "var(--sage-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <s.icon size={18} style={{ color: "var(--sage)" }} strokeWidth={1.5} />
              </div>
              <div>
                <p style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "Bricolage Grotesque, sans-serif", color: "var(--text-primary)", margin: 0 }}>{s.value}</p>
                <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: 0, marginTop: "0.25rem" }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1rem" }}>
            <div style={{ display: "flex" }}>
              {TABS.map(t => (
                <button key={t} data-testid={`tab-${t.replace(" ", "-").toLowerCase()}`} onClick={() => setTab(t)}
                  style={{
                    padding: "0.875rem 1rem", fontSize: "0.875rem", fontWeight: 500,
                    border: "none", background: "none", cursor: "pointer",
                    borderBottom: tab === t ? "2px solid var(--sage)" : "2px solid transparent",
                    color: tab === t ? "var(--sage)" : "var(--text-muted)", transition: "all 0.15s",
                  }}>
                  {t}
                </button>
              ))}
            </div>
            {tab === "Rule Sets" && (
              <div style={{ display: "flex", gap: "0.375rem" }}>
                <input ref={importRSRef} type="file" accept=".json" style={{ display: "none" }} onChange={e => handleImportRS(e.target.files?.[0])} />
                {selectedRS.size > 0 && (
                  <button data-testid="bulk-delete-rs" className="btn-secondary"
                    onClick={bulkDeleteRS}
                    style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem", color: "#991B1B", borderColor: "#FCA5A5" }}>
                    <Trash2 size={14} /> Delete {selectedRS.size}
                  </button>
                )}
                <button data-testid="import-rs-btn" className="btn-secondary"
                  onClick={() => importRSRef.current?.click()}
                  style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
                  <Upload size={14} /> Import
                </button>
                <button data-testid="export-all-rs-btn" className="btn-secondary"
                  onClick={() => downloadJSON("/rule-sets/export-all", "rule_sets_export.json")}
                  style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
                  <Download size={14} /> Export All
                </button>
                <button data-testid="new-ruleset-btn" className="btn-primary"
                  onClick={() => navigate("/rule-builder")}
                  style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
                  <Plus size={14} /> New Rule Set
                </button>
              </div>
            )}
            {tab === "Run History" && (
              <div style={{ display: "flex", gap: "0.375rem" }}>
                {selectedRuns.size > 0 && (
                  <button data-testid="bulk-delete-runs" className="btn-secondary"
                    onClick={bulkDeleteRuns}
                    style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem", color: "#991B1B", borderColor: "#FCA5A5" }}>
                    <Trash2 size={14} /> Delete {selectedRuns.size}
                  </button>
                )}
                {runs.filter(r => r.status === "completed").length >= 2 && (
                  <button data-testid="compare-runs-btn" className="btn-secondary"
                    onClick={() => { setShowCompare(true); setCompareResult(null); setCompareA(""); setCompareB(""); }}
                    style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
                    <GitCompareArrows size={14} /> Compare Runs
                  </button>
                )}
              </div>
            )}
            {tab === "Exceptions" && selectedEx.size > 0 && (
              <button data-testid="bulk-delete-ex" className="btn-secondary"
                onClick={bulkDeleteExceptions}
                style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem", color: "#991B1B", borderColor: "#FCA5A5" }}>
                <Trash2 size={14} /> Delete {selectedEx.size}
              </button>
            )}
          </div>

          {tab === "Rule Sets" ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th className="table-header" style={{ width: "32px" }}>
                    <input data-testid="rs-select-all" type="checkbox"
                      checked={ruleSets.length > 0 && selectedRS.size === ruleSets.length}
                      onChange={() => toggleAll(setSelectedRS, ruleSets)} />
                  </th>
                  {["Name", "Department", "Rules", "Status", "Created", "Actions"].map(h => (
                    <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ruleSets.map(rs => (
                  <tr key={rs.id} className="table-row">
                    <td className="table-cell" style={{ width: "32px" }}>
                      <input data-testid={`rs-check-${rs.id}`} type="checkbox"
                        checked={selectedRS.has(rs.id)} onChange={() => toggle(setSelectedRS, selectedRS, rs.id)} />
                    </td>
                    <td className="table-cell" style={{ fontWeight: 500 }}>{rs.name}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)" }}>{rs.department_name || "—"}</td>
                    <td className="table-cell">
                      <span style={{ background: "var(--bg-subtle)", borderRadius: "9999px", padding: "0.125rem 0.5rem", fontSize: "0.8rem", fontWeight: 600 }}>
                        {(rs.rules || []).length} rules
                      </span>
                    </td>
                    <td className="table-cell"><span className={rs.is_active ? "badge-matched" : "badge-unmatched"}>{rs.is_active ? "Active" : "Inactive"}</span></td>
                    <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                      {rs.created_at ? new Date(rs.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="table-cell">
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button data-testid={`export-rs-${rs.id}`} onClick={() => downloadJSON(`/rule-sets/${rs.id}/export`, `rule_set_${rs.name.replace(/\s+/g, "_")}.json`)} title="Export" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><Download size={14} /></button>
                        <button data-testid={`edit-rs-${rs.id}`} onClick={() => navigate(`/rule-builder/${rs.id}`)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><Edit2 size={14} /></button>
                        <button data-testid={`del-rs-${rs.id}`} onClick={() => deleteRS(rs.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {ruleSets.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>
                    No rule sets yet. <button onClick={() => navigate("/rule-builder")} style={{ color: "var(--sage)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Create one now</button>
                  </td></tr>
                )}
              </tbody>
            </table>
          ) : tab === "Run History" ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th className="table-header" style={{ width: "32px" }}>
                    <input data-testid="runs-select-all" type="checkbox"
                      checked={runs.length > 0 && selectedRuns.size === runs.length}
                      onChange={() => toggleAll(setSelectedRuns, runs)} />
                  </th>
                  {["Name", "Rule Set", "Files", "Match Rate", "Status", "Date", "Actions"].map(h => (
                    <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="table-row">
                    <td className="table-cell" style={{ width: "32px" }}>
                      <input data-testid={`run-check-${r.id}`} type="checkbox"
                        checked={selectedRuns.has(r.id)} onChange={() => toggle(setSelectedRuns, selectedRuns, r.id)} />
                    </td>
                    <td className="table-cell" style={{ fontWeight: 500 }}>{r.name}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)" }}>{r.rule_set_name}</td>
                    <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                      {r.file_a_name} / {r.file_b_name}
                    </td>
                    <td className="table-cell">
                      {r.summary ? (
                        <span style={{ fontWeight: 600, color: r.summary.match_rate >= 80 ? "#065F46" : r.summary.match_rate >= 50 ? "#92400E" : "#991B1B" }}>
                          {r.summary.match_rate}%
                        </span>
                      ) : "—"}
                    </td>
                    <td className="table-cell">{statusBadge(r.status)}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="table-cell">
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        {r.status === "completed" && (
                          <button data-testid={`view-run-${r.id}`} onClick={() => navigate(`/results/${r.id}`)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--sage)" }}>
                            <Eye size={14} />
                          </button>
                        )}
                        <button data-testid={`rerun-run-${r.id}`} title="Re-run with same config"
                          onClick={async () => { if (!window.confirm(`Re-run "${r.name}"?`)) return; try { const res = await api.post(`/reconciliation/runs/${r.id}/rerun`); navigate(`/results/${res.data.id}`); } catch (e) { alert(e.response?.data?.detail || e.message); } }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#1E40AF" }}>
                          <RotateCw size={14} />
                        </button>
                        <button data-testid={`del-run-${r.id}`}
                          onClick={async () => { if (window.confirm(`Delete run "${r.name}" and all its results? This cannot be undone.`)) { await api.delete(`/reconciliation/runs/${r.id}`); load(); } }}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>No runs yet</td></tr>
                )}
              </tbody>
            </table>
          ) : tab === "Multi-file Runs" ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Name", "Anchor file", "Targets", "Matched / Conflicts / Unmatched", "Created", "Actions"].map(h => (
                    <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {multiRuns.map(mr => {
                  const s = mr.summary || {};
                  return (
                    <tr key={mr.id} className="table-row" data-testid={`multirun-row-${mr.id}`}>
                      <td className="table-cell" style={{ fontWeight: 500 }}>{mr.name}</td>
                      <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{mr.anchor_file_name}</td>
                      <td className="table-cell" style={{ fontSize: "0.8rem" }}>{(mr.legs || []).length} files</td>
                      <td className="table-cell" style={{ fontSize: "0.8rem" }}>
                        <span style={{ color: "#065F46", fontWeight: 600 }}>{s.matched_single || 0}</span>
                        {" / "}
                        <span style={{ color: "#B45309", fontWeight: 600 }}>{s.matched_conflict || 0}</span>
                        {" / "}
                        <span style={{ color: "#991B1B", fontWeight: 600 }}>{s.unmatched_anchor || 0}</span>
                      </td>
                      <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{mr.created_at ? new Date(mr.created_at).toLocaleDateString() : "—"}</td>
                      <td className="table-cell">
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button data-testid={`view-multirun-${mr.id}`} onClick={() => navigate(`/multi-results/${mr.id}`)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--sage)" }}>
                            <Eye size={14} />
                          </button>
                          <button data-testid={`rerun-multirun-${mr.id}`} title="Re-run with same config"
                            onClick={async () => { if (!window.confirm(`Re-run multi-run "${mr.name}"?`)) return; try { const res = await api.post(`/multi-runs/${mr.id}/rerun`); navigate(`/multi-results/${res.data.id}`); } catch (e) { alert(e.response?.data?.detail || e.message); } }}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#1E40AF" }}>
                            <RotateCw size={14} />
                          </button>
                          <button data-testid={`del-multirun-${mr.id}`}
                            onClick={async () => { if (window.confirm(`Delete multi-run "${mr.name}"? This removes all legs and results.`)) { await api.delete(`/multi-runs/${mr.id}`); load(); } }}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {multiRuns.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>
                    No multi-file runs yet. <button onClick={() => navigate("/multi-reconcile")} style={{ color: "var(--sage)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Start one</button>
                  </td></tr>
                )}
              </tbody>
            </table>
          ) : tab === "Chain Runs" ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="chain-runs-table">
              <thead>
                <tr>
                  {["Name", "Anchor", "Hops", "Complete / Broken / Conflict", "Cost reconciled", "Created", "Actions"].map(h => (
                    <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chainRuns.map(cr => {
                  const s = cr.summary || {};
                  return (
                    <tr key={cr.id} className="table-row" data-testid={`chainrun-row-${cr.id}`}>
                      <td className="table-cell" style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <Link2 size={12} style={{ color: "#8B5CF6" }} /> {cr.name}
                      </td>
                      <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{cr.anchor_file_name}</td>
                      <td className="table-cell" style={{ fontSize: "0.8rem" }}>{(cr.legs || []).length}</td>
                      <td className="table-cell" style={{ fontSize: "0.8rem" }}>
                        <span style={{ color: "#065F46", fontWeight: 600 }}>{s.chains_complete || 0}</span>
                        {" / "}
                        <span style={{ color: "#991B1B", fontWeight: 600 }}>{s.chains_broken || 0}</span>
                        {" / "}
                        <span style={{ color: "#9A3412", fontWeight: 600 }}>{s.chains_conflict || 0}</span>
                        {s.anomaly_flag && <span title={s.anomaly_reason} style={{ marginLeft: "0.4rem", color: "#F59E0B" }}>⚠</span>}
                      </td>
                      <td className="table-cell" style={{ fontSize: "0.8rem", color: "#0891B2", fontWeight: 600 }}>
                        ${Number(s.total_cost_complete || 0).toLocaleString()}
                      </td>
                      <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                        {cr.created_at ? new Date(cr.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="table-cell">
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button data-testid={`view-chainrun-${cr.id}`} onClick={() => navigate(`/chain-results/${cr.id}`)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--sage)" }}>
                            <Eye size={14} />
                          </button>
                          <button data-testid={`rerun-chainrun-${cr.id}`} title="Re-run with same config"
                            onClick={async () => { if (!window.confirm(`Re-run chain "${cr.name}"?`)) return; try { const res = await api.post(`/chain-runs/${cr.id}/rerun`); navigate(`/chain-results/${res.data.id}`); } catch (e) { alert(e.response?.data?.detail || e.message); } }}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#1E40AF" }}>
                            <RotateCw size={14} />
                          </button>
                          <button data-testid={`del-chainrun-${cr.id}`}
                            onClick={async () => { if (!window.confirm(`Delete chain run "${cr.name}"? This removes all hops and results.`)) return; await api.delete(`/chain-runs/${cr.id}`); load(); }}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {chainRuns.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>
                    No chain runs yet. <button onClick={() => navigate("/chain-reconcile")} style={{ color: "var(--sage)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Start one</button>
                  </td></tr>
                )}
              </tbody>
            </table>
          ) : tab === "Exceptions" ? (
            <div data-testid="exceptions-tab">
              <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Flag size={14} style={{ color: "var(--sage)" }} />
                <span style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{exceptions.length} exceptions</span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th className="table-header" style={{ width: "32px" }}>
                      <input data-testid="ex-select-all" type="checkbox"
                        checked={exceptions.length > 0 && selectedEx.size === exceptions.length}
                        onChange={() => toggleAll(setSelectedEx, exceptions)} />
                    </th>
                    {["Run", "Notes", "Flag", "Assignee", "Reported By", "Updated", "Actions"].map(h => (
                      <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exceptions.map(ex => {
                    const c = FLAG_COLORS[ex.flag] || FLAG_COLORS.review;
                    const runName = (runs.find(r => r.id === ex.run_id) || {}).name || (ex.run_id ? ex.run_id.slice(-8) : "—");
                    return (
                      <tr key={ex.id} className="table-row" data-testid={`exception-row-${ex.id}`}>
                        <td className="table-cell" style={{ width: "32px" }}>
                          <input data-testid={`ex-check-${ex.id}`} type="checkbox"
                            checked={selectedEx.has(ex.id)} onChange={() => toggle(setSelectedEx, selectedEx, ex.id)} />
                        </td>
                        <td className="table-cell" style={{ fontSize: "0.82rem" }}>
                          <button onClick={() => navigate(`/results/${ex.run_id}`)}
                            style={{ color: "var(--sage)", background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 500, textAlign: "left" }}>
                            {runName}
                          </button>
                        </td>
                        <td className="table-cell" style={{ fontSize: "0.78rem", color: "var(--text-secondary)", maxWidth: "240px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ex.notes || <em style={{ color: "var(--text-muted)" }}>no notes</em>}
                        </td>
                        <td className="table-cell">
                          <select data-testid={`exception-flag-${ex.id}`}
                            value={ex.flag}
                            onChange={e => transitionException(ex.id, e.target.value)}
                            className="recon-input"
                            style={{ fontSize: "0.72rem", padding: "0.125rem 0.375rem", background: c.bg, color: c.fg, border: `1px solid ${c.border}`, fontWeight: 600 }}>
                            {Object.keys(FLAG_COLORS).map(f => <option key={f} value={f}>{f.replace("_", " ")}</option>)}
                          </select>
                        </td>
                        <td className="table-cell">
                          <select data-testid={`exception-assign-${ex.id}`}
                            value={ex.assigned_to_email || ""}
                            onChange={e => assignException(ex.id, e.target.value)}
                            className="recon-input"
                            style={{ fontSize: "0.75rem", padding: "0.125rem 0.375rem", maxWidth: "180px" }}>
                            <option value="">— unassigned —</option>
                            {deptUsers.map(u => <option key={u.id} value={u.email}>{u.email}</option>)}
                          </select>
                        </td>
                        <td className="table-cell" style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{ex.created_by_email || "—"}</td>
                        <td className="table-cell" style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                          {ex.updated_at ? new Date(ex.updated_at).toLocaleDateString() : "—"}
                          {ex.status_history && ex.status_history.length > 1 && (
                            <div style={{ fontSize: "0.68rem", color: "var(--sage)", marginTop: "0.125rem" }} title={`${ex.status_history.length} status changes`}>
                              {ex.status_history.length} transitions
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <button data-testid={`exception-delete-${ex.id}`} onClick={() => deleteException(ex.id)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {exceptions.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>No exceptions flagged yet. Flag rows from the Results page.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>

      {/* Compare Runs Modal */}
      <Modal open={showCompare} onClose={() => { setShowCompare(false); setDrilldown(null); }} title="Compare Two Runs" width={720}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Run A (baseline)</label>
            <select data-testid="compare-a" className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }}
              value={compareA} onChange={e => setCompareA(e.target.value)}>
              <option value="">— select run —</option>
              {runs.filter(r => r.status === "completed").map(r => (
                <option key={r.id} value={r.id}>{r.name} ({new Date(r.created_at).toLocaleDateString()})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Run B (compare)</label>
            <select data-testid="compare-b" className="recon-input" style={{ width: "100%", fontSize: "0.85rem" }}
              value={compareB} onChange={e => setCompareB(e.target.value)}>
              <option value="">— select run —</option>
              {runs.filter(r => r.status === "completed").map(r => (
                <option key={r.id} value={r.id}>{r.name} ({new Date(r.created_at).toLocaleDateString()})</option>
              ))}
            </select>
          </div>
        </div>
        <button data-testid="compare-go" onClick={runComparison} disabled={!compareA || !compareB || compareA === compareB}
          className="btn-primary" style={{ fontSize: "0.85rem", marginBottom: "1rem" }}>
          Compare
        </button>
        {compareResult && (
          <div data-testid="compare-result" style={{ borderTop: "1px solid var(--border-default)", paddingTop: "0.75rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", marginBottom: "0.75rem" }}>
              {[
                { lbl: "Δ Matched", val: compareResult.delta.matched, good: compareResult.delta.matched > 0 },
                { lbl: "Δ Match Rate", val: `${compareResult.delta.match_rate}%`, good: compareResult.delta.match_rate >= 0 },
                { lbl: "Δ Partial", val: compareResult.delta.partial },
                { lbl: "Δ Unmatched A", val: compareResult.delta.unmatched_a, good: compareResult.delta.unmatched_a <= 0 },
              ].map((d, i) => (
                <div key={i} style={{ padding: "0.5rem 0.625rem", background: "var(--bg-subtle)", borderRadius: "0.375rem", textAlign: "center" }}>
                  <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.125rem" }}>{d.lbl}</div>
                  <div style={{ fontSize: "1.1rem", fontWeight: 600, color: d.good === undefined ? "var(--text-primary)" : d.good ? "#065F46" : "#991B1B", fontFamily: "Bricolage Grotesque, sans-serif" }}>
                    {typeof d.val === "number" && d.val > 0 ? "+" : ""}{d.val}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", fontSize: "0.82rem" }}>
              {[
                { key: "improved", label: "Improved", bg: "#F0FDF4", fg: "#065F46", border: "#BBF7D0", items: compareResult.transitions.improved, count: compareResult.transitions.improved_count },
                { key: "regressed", label: "Regressed", bg: "#FEF2F2", fg: "#991B1B", border: "#FECACA", items: compareResult.transitions.regressed, count: compareResult.transitions.regressed_count },
                { key: "new_in_b", label: "New in B", bg: "#EFF6FF", fg: "#1E40AF", border: "#BFDBFE", items: compareResult.transitions.new_in_b, count: compareResult.transitions.new_count },
                { key: "dropped_from_a", label: "Dropped from A", bg: "#FFF7ED", fg: "#9A3412", border: "#FED7AA", items: compareResult.transitions.dropped_from_a, count: compareResult.transitions.dropped_count },
              ].map(cat => (
                <button
                  key={cat.key}
                  data-testid={`drilldown-${cat.key}`}
                  onClick={() => setDrilldown(drilldown?.key === cat.key ? null : { key: cat.key, label: cat.label, items: cat.items, fg: cat.fg })}
                  style={{ padding: "0.625rem 0.75rem", background: cat.bg, borderRadius: "0.375rem", border: `1px solid ${drilldown?.key === cat.key ? cat.fg : cat.border}`, cursor: cat.count > 0 ? "pointer" : "default", textAlign: "left", fontSize: "0.82rem", opacity: cat.count === 0 ? 0.6 : 1 }}
                  disabled={cat.count === 0}
                >
                  <strong style={{ color: cat.fg }}>{cat.label}:</strong> {cat.count}
                  {cat.count > 0 && <span style={{ float: "right", color: cat.fg, fontSize: "0.75rem" }}>{drilldown?.key === cat.key ? "▾" : "▸"}</span>}
                </button>
              ))}
            </div>
            {drilldown && drilldown.items && drilldown.items.length > 0 && (
              <div data-testid="drilldown-panel" style={{ marginTop: "0.75rem", padding: "0.75rem", background: "var(--bg-subtle)", borderRadius: "0.5rem", maxHeight: "320px", overflowY: "auto" }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 600, color: drilldown.fg, marginBottom: "0.5rem" }}>
                  {drilldown.label}: {drilldown.items.length} rows
                </div>
                {drilldown.items.slice(0, 50).map((it, i) => (
                  <div key={i} style={{ fontSize: "0.72rem", padding: "0.375rem 0.5rem", background: "white", borderRadius: "0.375rem", marginBottom: "0.25rem", border: "1px solid var(--border-default)" }}>
                    <div style={{ fontFamily: "JetBrains Mono, monospace", color: "var(--text-muted)", marginBottom: "0.2rem" }}>
                      key: {it.key}
                    </div>
                    {it.was && it.now && (
                      <div style={{ fontSize: "0.7rem" }}>
                        <span style={{ background: "#FEE2E2", padding: "1px 6px", borderRadius: "9999px" }}>{it.was}</span>
                        <span style={{ margin: "0 0.375rem" }}>→</span>
                        <span style={{ background: "#D1FAE5", padding: "1px 6px", borderRadius: "9999px" }}>{it.now}</span>
                      </div>
                    )}
                    {it.status && !it.was && (
                      <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>status: {it.status}</div>
                    )}
                    {it.row && (
                      <div style={{ marginTop: "0.2rem", fontFamily: "JetBrains Mono, monospace", fontSize: "0.68rem", color: "var(--text-muted)" }}>
                        {Object.entries(it.row).slice(0, 4).map(([k, v]) => (
                          <span key={k} style={{ marginRight: "0.625rem" }}>
                            <span style={{ color: "var(--text-muted)" }}>{k}:</span> {String(v)?.slice(0, 30)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {drilldown.items.length > 50 && (
                  <div style={{ padding: "0.5rem", textAlign: "center", fontSize: "0.72rem", color: "var(--text-muted)" }}>
                    Showing first 50 of {drilldown.items.length}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}
